"use strict";
/**
 * Time-distribution presentation model — the single shared view model behind
 * the "WHERE DID THE WORKER'S RECORDED TIME GO?" question
 * (NORMAX_PERFORMANCE_TIME_DISTRIBUTION_SIMPLIFICATION_FORENSIC_AND_IMPLEMENTATION_REPORT.md).
 *
 *   totalRecordedMinutes = normedMinutes + neutralMinutes + unnormedMinutes
 *   normedShare   = normedMinutes   / totalRecordedMinutes   (ratio 0..1)
 *   neutralShare  = neutralMinutes  / totalRecordedMinutes
 *   unnormedShare = unnormedMinutes / totalRecordedMinutes
 *
 * This is deliberately SEPARATE from performance / Method C: earned standard
 * minutes NEVER enter this sum. Performance ("Učinak") answers a different
 * question ("how well on the normed portion") and is presented on its own.
 *
 * Pure presentation layer, no rounding of business values, no external
 * dependency (same discipline as the rest of `@normax-performance/shared-format`).
 * Minutes are summed with decimal-safe integer scaling so the authoritative
 * `normed + neutral + unnormed = total` identity never picks up binary
 * floating-point dust. Shares are raw ratios — each surface (Analytics,
 * Reports, Dashboard, PDF) formats them for display through ONE shared
 * `formatSharePercent` helper so the rounding rule stays consistent.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildTimeDistribution = buildTimeDistribution;
exports.formatSharePercent = formatSharePercent;
function decimalPlaces(value) {
    const dot = value.indexOf(".");
    return dot === -1 ? 0 : value.length - dot - 1;
}
/** Trims a fixed-notation string of trailing zeros / a dangling decimal point ("450.00" -> "450", "63.60" -> "63.6"). */
function trimFixed(value) {
    if (!value.includes("."))
        return value;
    return value.replace(/\.?0+$/, "");
}
/** Decimal-safe sum of minute values given as strings — scales every operand
 * to a common integer basis, adds with BigInt, then rescales. Deterministic
 * and dependency-free (a full Decimal library would be overkill here). */
function sumMinutes(values) {
    const scale = values.reduce((max, v) => Math.max(max, decimalPlaces(v)), 0);
    const factor = 10n ** BigInt(scale);
    let total = 0n;
    for (const v of values) {
        const negative = v.trim().startsWith("-");
        const [intPart, fracPart = ""] = v.trim().replace("-", "").split(".");
        const scaledFrac = (fracPart + "0".repeat(scale)).slice(0, scale);
        const scaled = BigInt(intPart || "0") * factor + BigInt(scaledFrac || "0");
        total += negative ? -scaled : scaled;
    }
    if (scale === 0)
        return total.toString();
    const negative = total < 0n;
    const abs = (negative ? -total : total).toString().padStart(scale + 1, "0");
    const intOut = abs.slice(0, abs.length - scale);
    const fracOut = abs.slice(abs.length - scale);
    return `${negative ? "-" : ""}${trimFixed(`${intOut}.${fracOut}`)}`;
}
/** Builds the shared time-distribution view model. Safe for a zero total
 * (all three shares become `null` — never `NaN`/`Infinity`, never a
 * misleading 0% split for a period where nothing was recorded). */
function buildTimeDistribution(input) {
    const normedMinutes = trimFixed(String(input.normedMinutes));
    const neutralMinutes = trimFixed(String(input.neutralMinutes));
    const unnormedMinutes = trimFixed(String(input.unnormedMinutes));
    const totalRecordedMinutes = sumMinutes([normedMinutes, neutralMinutes, unnormedMinutes]);
    const total = Number(totalRecordedMinutes);
    const hasTotal = Number.isFinite(total) && total > 0;
    return {
        normedMinutes,
        neutralMinutes,
        unnormedMinutes,
        totalRecordedMinutes,
        normedShare: hasTotal ? Number(normedMinutes) / total : null,
        neutralShare: hasTotal ? Number(neutralMinutes) / total : null,
        unnormedShare: hasTotal ? Number(unnormedMinutes) / total : null,
    };
}
/**
 * The ONE shared rule for rendering a time-distribution share as a percent
 * label — used by Analytics, Reports, Dashboard and the PDF so the number
 * reads identically everywhere. `null` share (zero total) -> `"—"`.
 *
 * `digits` defaults to 1 (the authoritative summary presentation, e.g.
 * "63,6%"); the dense Analytics worker-row bar passes `0` for a compact
 * integer legend ("64%"). Comma decimal separator, matching every other
 * business-calculation value in this package.
 */
function formatSharePercent(share, digits = 1) {
    if (share === null || !Number.isFinite(share))
        return "—";
    const percent = (share * 100).toFixed(digits);
    return `${percent.replace(".", ",")}%`;
}
//# sourceMappingURL=time-distribution.js.map